import type { Me, WebLoginResult, WebTokens } from '@emc/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, refreshSession, session } from './api';

type Status = 'loading' | 'anonymous' | 'needs-mfa' | 'authenticated';

interface Auth {
  status: Status;
  me: Me | null;
  can: (permission: string) => boolean;
  login: (email: string, password: string) => Promise<{ mfaToken: string } | null>;
  verifyMfa: (mfaToken: string, code: string) => Promise<void>;
  setupMfa: () => Promise<{ secret: string; otpauthUrl: string }>;
  confirmMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const qc = useQueryClient();

  const reset = useCallback(() => {
    session.clear();
    setMe(null);
    setStatus('anonymous');
    qc.clear();
  }, [qc]);

  const loadMe = useCallback(async () => {
    const m = await api<Me>('/me');
    if (!m.roles.length) {
      await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
      reset();
      throw new ApiError(403, 'forbidden', 'Esta cuenta no tiene acceso al panel.');
    }
    setMe(m);
    setStatus(m.mfaEnabled ? 'authenticated' : 'needs-mfa');
  }, [reset]);

  useEffect(() => {
    void (async () => {
      if (await refreshSession()) await loadMe().catch(() => reset());
      else setStatus('anonymous');
    })();
    return session.onExpire(reset);
  }, [loadMe, reset]);

  const start = async (t: WebTokens) => {
    session.set(t);
    await loadMe();
  };

  const value: Auth = {
    status,
    me,
    can: (p) => !!me?.permissions.includes(p as never),
    login: async (email, password) => {
      const r = await api<WebLoginResult>('/auth/login', { body: { email, password } });
      if (r.mfaRequired) return { mfaToken: r.mfaToken };
      await start(r);
      return null;
    },
    verifyMfa: async (mfaToken, code) => start(await api<WebTokens>('/auth/mfa/verify', { body: { mfaToken, code } })),
    setupMfa: () => api('/auth/mfa/setup', { method: 'POST', body: {} }),
    confirmMfa: async (code) => start(await api<WebTokens>('/auth/mfa/confirm', { body: { code } })),
    logout: async () => {
      await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
      reset();
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const a = useContext(Ctx);
  if (!a) throw new Error('useAuth fuera de AuthProvider');
  return a;
}
